import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { updateSEOMeta, resetSEOMeta, useBlogLanguage } from '../utils/seoUtils';

const WHATSAPP_LINK = "https://wa.me/529841585732?text=Hola%2C%20quiero%20agendar%20una%20consulta%20gratuita%20con%20Marea%20Riviera";

export default function BlogTulumVsPlaya() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';
  useBlogLanguage('es');

  useEffect(() => {
    updateSEOMeta({
      title: es ? 'Tulum vs Playa del Carmen: ¿Dónde Invertir en 2026? | Marea Riviera' : 'Tulum vs Playa del Carmen: Where to Invest in 2026? | Marea Riviera',
      description: es ? '¿Tulum o Playa del Carmen? Comparamos ROI, precios, plusvalía y perfil de renta para ayudarte a elegir dónde poner tu dinero en la Riviera Maya.' : 'Tulum or Playa del Carmen? We compare ROI, prices, appreciation and rental profiles to help you choose where to invest in the Riviera Maya.',
      url: 'https://mareariviera.com/blog/tulum-vs-playa-del-carmen-donde-invertir',
      lang: es ? 'es' : 'en'
    });
    window.scrollTo(0, 0);
    return () => { resetSEOMeta(); };
  }, [es]);

  const scrollToContact = () => { sessionStorage.setItem('scrollTo', 'contact'); navigate('/'); };

  return (
    <div className="bg-[#FAFAF7] min-h-screen pt-[120px]">
      {/* Hero */}
      <div className="bg-[#0A2235] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#2BBFBF] hover:text-[#7ECFCF] transition-colors mb-8 font-inter text-sm"
            data-testid="blog-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {es ? 'Volver al inicio' : 'Back to home'}
          </button>
          <div className="flex items-center gap-4 text-[#FAFAF7]/50 font-inter text-sm mb-6">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {es ? '11 mayo 2026' : 'May 11, 2026'}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {es ? '8 min lectura' : '8 min read'}</span>
          </div>
          <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#FAFAF7] leading-tight mb-6" data-testid="blog-title">
            {es ? 'Tulum vs Playa del Carmen: ¿Dónde Invertir en Bienes Raíces en 2026?' : 'Tulum vs Playa del Carmen: Where to Invest in Real Estate in 2026?'}
          </h1>
          <p className="font-inter text-[#FAFAF7]/70 text-lg leading-relaxed">
            {es ? 'Comparamos precios, rendimientos, plusvalía y perfil de renta para ayudarte a elegir dónde poner tu dinero en la Riviera Maya.' : 'We compare prices, yields, appreciation and rental profiles to help you choose where to invest in the Riviera Maya.'}
          </p>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="blog-content">
        <div className="prose-marea">
          <p>Si estás buscando invertir en la Riviera Maya, tarde o temprano llegas a la misma pregunta: <strong>¿Tulum o Playa del Carmen?</strong></p>
          <p>Ambas ciudades están en el mismo corredor turístico, ambas tienen demanda de renta vacacional, y en ambas se están construyendo desarrollos con rendimientos atractivos. Pero no son iguales — y dependiendo de tu perfil como inversionista, una puede ser mucho mejor opción que la otra.</p>
          <p>En este artículo comparamos las dos ciudades en los factores que más importan: precio de entrada, rendimiento, plusvalía, perfil de inquilino y riesgo. Sin adornos. Para una visión más amplia del mercado, consulta nuestra <a href="/blog/inversion-inmobiliaria-riviera-maya" className="text-[#2BBFBF] underline hover:text-[#7ECFCF] transition-colors">guía completa de inversión en Riviera Maya</a>.</p>

          <h2>El Contexto: Dos Mercados Muy Diferentes</h2>
          <p>Tulum y Playa del Carmen comparten la misma autopista federal, pero son mercados con dinámicas distintas.</p>
          <p><strong>Playa del Carmen</strong> es una ciudad consolidada. Con más de 300,000 habitantes, infraestructura urbana madura, aeropuerto internacional próximo y una economía local que va más allá del turismo. Es el centro de operaciones de la Riviera Maya desde hace 30 años.</p>
          <p><strong>Tulum</strong> es un mercado en expansión acelerada. De ser un pueblo bohemio pasó a convertirse en uno de los destinos de lujo más buscados del mundo. Tiene su propio aeropuerto internacional inaugurado en 2024, un Tren Maya que lo conecta con Cancún, y una narrativa de marca global que pocos destinos en México tienen.</p>
          <p>Cada uno tiene sus ventajas. La clave está en saber qué tipo de inversionista eres tú.</p>

          <h2>1. Precio de Entrada</h2>
          <div className="overflow-x-auto my-8">
            <table>
              <thead>
                <tr><th></th><th>Tulum</th><th>Playa del Carmen</th></tr>
              </thead>
              <tbody>
                <tr><td>Precio promedio por m²</td><td>$3,500 – $6,000 USD</td><td>$2,200 – $4,500 USD</td></tr>
                <tr><td>Ticket mínimo de entrada</td><td>~$150,000 USD</td><td>~$80,000 USD</td></tr>
                <tr><td>Preventa desde</td><td>~$120,000 USD</td><td>~$65,000 USD</td></tr>
              </tbody>
            </table>
          </div>
          <p><strong>Playa del Carmen gana</strong> en accesibilidad. Puedes entrar al mercado con menos capital y diversificar en dos propiedades mientras en Tulum apenas llegas a una.</p>
          <p>Tulum, sin embargo, tiene desarrollos de lujo donde el precio por metro cuadrado ya supera los $5,000 USD — y la demanda los soporta.</p>

          <h2>2. Rendimiento y Renta Vacacional</h2>
          <p>Esta es la pregunta que más importa: <strong>¿cuánto deja mensualmente?</strong></p>
          
          <h3>Tulum</h3>
          <ul>
            <li>Ocupación promedio Airbnb: 70–85% en temporada alta (diciembre–abril)</li>
            <li>Tarifa promedio por noche: $120–$350 USD (según categoría)</li>
            <li>Rendimiento bruto estimado: <strong>10–18% anual</strong></li>
            <li>Perfil de huésped: turista internacional de alto poder adquisitivo, estadías de 4–10 noches</li>
          </ul>

          <h3>Playa del Carmen</h3>
          <ul>
            <li>Ocupación promedio Airbnb: 65–80% en temporada alta</li>
            <li>Tarifa promedio por noche: $80–$200 USD</li>
            <li>Rendimiento bruto estimado: <strong>8–14% anual</strong></li>
            <li>Perfil de huésped: más variado — turismo nacional, nómadas digitales, familias, temporadas más largas</li>
          </ul>
          <p><strong>Tulum gana</strong> en rendimiento bruto, pero con mayor estacionalidad. En temporada baja (mayo–octubre), la ocupación puede caer al 40–50%. Playa del Carmen tiene una demanda más estable durante todo el año por su base de residentes y nómadas digitales.</p>

          <h2>3. Plusvalía</h2>
          <p>Aquí está uno de los argumentos más fuertes a favor de Tulum.</p>
          <p>En los últimos 5 años, el valor de los inmuebles en Tulum creció entre <strong>60% y 120%</strong> dependiendo de la zona. La apertura del aeropuerto Felipe Carrillo Puerto en 2024 y la consolidación del Tren Maya están impulsando una segunda ola de valorización.</p>
          <p>Playa del Carmen también ha tenido plusvalía sostenida — entre <strong>30% y 60% en el mismo periodo</strong> — especialmente en zonas como Playacar, el corredor de Constituyentes y las nuevas colonias al norte de la ciudad.</p>
          <p><strong>Tulum gana</strong> en plusvalía esperada, especialmente en zonas como Aldea Zamá, La Veleta y el corredor hacia Tulum Pueblo. Pero con esa oportunidad viene mayor volatilidad y mayor dependencia de que el boom continúe.</p>

          <h2>4. Estabilidad y Riesgo</h2>
          <p>Ninguna inversión inmobiliaria es libre de riesgo, y la Riviera Maya no es la excepción.</p>
          
          <h3>Tulum: mayor oportunidad, mayor riesgo</h3>
          <ul>
            <li>Mercado más joven y más volátil</li>
            <li>Regulaciones en proceso (zona federal costera, restricciones ecológicas)</li>
            <li>Dependencia alta del turismo internacional</li>
            <li>Saturación de oferta en algunas zonas ya es visible</li>
            <li>Infraestructura urbana aún en desarrollo (agua, drenaje, vialidades)</li>
          </ul>

          <h3>Playa del Carmen: más estable, más predecible</h3>
          <ul>
            <li>Ciudad consolidada con economía diversificada</li>
            <li>Marco regulatorio más maduro</li>
            <li>Múltiples fuentes de demanda (turismo, residentes, nómadas digitales, expatriados)</li>
            <li>Menor riesgo de sobreoferta en zonas consolidadas</li>
            <li>Servicios urbanos completos</li>
          </ul>
          <p><strong>Playa del Carmen gana</strong> en estabilidad. Si eres inversionista conservador o es tu primera propiedad en la región, Playa te da más certeza.</p>

          <h2>5. ¿Qué Perfil de Inversionista Va Mejor en Cada Uno?</h2>
          
          <h3>Invierte en Tulum si:</h3>
          <ul>
            <li>Buscas maximizar plusvalía a mediano plazo (5–10 años)</li>
            <li>Tienes capital para una propiedad de ticket alto</li>
            <li>Toleras mayor volatilidad en renta mensual</li>
            <li>Quieres posicionarte en un mercado de lujo con demanda internacional</li>
            <li>Es tu segunda o tercera propiedad de inversión</li>
          </ul>

          <h3>Invierte en Playa del Carmen si:</h3>
          <ul>
            <li>Es tu primera inversión inmobiliaria en la Riviera Maya</li>
            <li>Buscas flujo de caja estable desde el primer año</li>
            <li>Tienes un presupuesto más acotado y quieres diversificar</li>
            <li>Prefieres un mercado con mayor liquidez (más fácil vender si necesitas)</li>
            <li>Quieres opciones de renta tanto vacacional como de largo plazo</li>
          </ul>

          <h2>La Respuesta Honesta</h2>
          <p>No hay una respuesta única. Depende de tu capital, tu horizonte de inversión y tu tolerancia al riesgo.</p>
          <p>Lo que sí es claro: <strong>ambas ciudades ofrecen rendimientos que difícilmente encuentras en otros mercados de México o Latinoamérica</strong>. Mientras las tasas bancarias rondan el 10% anual, una propiedad bien seleccionada en cualquiera de las dos puede darte el doble — con la ventaja de que el activo se valoriza al mismo tiempo.</p>
          <p>La clave no es el destino. Es elegir el desarrollo correcto, con la estructura legal correcta, y con un operador que te garantice la renta desde el primer mes.</p>
        </div>

        {/* CTA */}
        <div className="mt-16 bg-[#0A2235] rounded-[20px] p-8 md:p-12 text-center">
          <h2 className="font-playfair text-2xl md:text-3xl text-[#FAFAF7] mb-4">
            {es ? '¿Quieres Saber Cuál Es la Mejor Opción Para Ti?' : 'Want to Know Which Is the Best Option for You?'}
          </h2>
          <p className="font-inter text-[#FAFAF7]/70 mb-8 max-w-xl mx-auto leading-relaxed">
            {es ? 'En Marea Riviera analizamos tu perfil de inversionista y te presentamos opciones en ambos destinos — con proyecciones de rendimiento reales y acompañamiento durante todo el proceso.' : 'At Marea Riviera we analyze your investor profile and present options in both destinations — with real yield projections and support throughout the process.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-[#2BBFBF] hover:bg-[#7ECFCF] text-[#0A2235] font-medium rounded-full px-8 py-6 text-base"
              data-testid="blog-cta-whatsapp"
            >
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                {es ? 'Agenda una consulta gratuita' : 'Schedule a free consultation'}
              </a>
            </Button>
            <Button
              onClick={scrollToContact}
              variant="outline"
              className="border-[#2BBFBF] text-[#2BBFBF] hover:bg-[#2BBFBF] hover:text-[#0A2235] rounded-full px-8 py-6 text-base"
              data-testid="blog-cta-contact"
            >
              {es ? 'Ir al formulario de contacto' : 'Go to contact form'}
            </Button>
          </div>
          <p className="font-inter text-[#FAFAF7]/40 text-sm mt-6">{es ? 'Sin compromiso. Respuesta en menos de 2 horas.' : 'No commitment. Response within 2 hours.'}</p>
        </div>

        {/* Author */}
        <div className="mt-12 pt-8 border-t border-[#0A2235]/10">
          <p className="font-inter text-[#0A2235]/50 text-sm italic leading-relaxed">
            Marea Riviera es una empresa de inversión inmobiliaria especializada en Tulum, Playa del Carmen y Puerto Morelos. Trabajamos exclusivamente con desarrollos que cuentan con MIA vigente y estructura legal sólida.
          </p>
        </div>
      </article>
    </div>
  );
}
